import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { SeedMap } from "../../shared/seedKeys";

export {
  HOME_KEY,
  PAGE_SIZE,
  TESTIMONIALS_KEY,
  categoryMetaKey,
  compareKey,
  directoryKey,
  entityKey,
  reviewsKey,
  userKey,
} from "../../shared/seedKeys";
export type { SeedMap } from "../../shared/seedKeys";

// Data seeded by the server render. On the first paint the value is already
// present, so the component renders real content with no effect and no flash —
// which is what makes the server and client markup identical. On a client-side
// navigation there is no seed, so the hook fetches as before.

const SeedContext = createContext<SeedMap>({});

export function DataProvider({ seed, children }: { seed: SeedMap; children: ReactNode }) {
  return <SeedContext.Provider value={seed}>{children}</SeedContext.Provider>;
}

/** Reads the payload the server embedded in the document. */
export function readSeed(): SeedMap {
  if (typeof document === "undefined") return {};
  const el = document.getElementById("__CC_DATA__");
  if (!el?.textContent) return {};
  try {
    return JSON.parse(el.textContent) as SeedMap;
  } catch {
    return {};
  }
}

export interface RouteData<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/**
 * `key` identifies this route's payload (e.g. `entity:token/ethereum`). The seed
 * is only valid for the key the server actually rendered; navigating to any other
 * key falls through to fetching.
 */
export function useRouteData<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts: { enabled?: boolean } = {},
): RouteData<T> {
  // `enabled` exists because a dependent fetch must not run before the thing it
  // depends on has loaded. Without it, a fetcher that reads another request's
  // result throws the moment the server seed is absent — which happens on every
  // SPA-fallback render, and took the whole page down with it.
  const enabled = opts.enabled ?? true;
  const seed = useContext(SeedContext);
  const seeded = seed[key] as T | undefined;

  const [state, setState] = useState<{ key: string; data: T | null; error: string | null; loading: boolean }>(
    () => ({ key, data: seeded ?? null, error: null, loading: seeded === undefined }),
  );

  useEffect(() => {
    if (!enabled) return;
    // Already satisfied for this key — either from the seed or a completed fetch.
    if (state.key === key && (state.data !== null || state.error !== null)) return;

    let cancelled = false;
    setState({ key, data: null, error: null, loading: true });
    fetcher()
      .then((d) => {
        if (!cancelled) setState({ key, data: d, error: null, loading: false });
      })
      .catch((err) => {
        if (!cancelled) setState({ key, data: null, error: (err as Error).message, loading: false });
      });
    return () => {
      cancelled = true;
    };
    // `fetcher` is a new closure each render; the real dependencies are the key
    // and whether the fetch is allowed to run yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // While a new key is mid-flight, never return the previous key's data.
  if (state.key !== key) return { data: null, error: null, loading: true };
  // Not started yet because its dependency has not loaded: pending, not failed.
  if (!enabled && state.data === null && state.error === null) {
    return { data: null, error: null, loading: true };
  }
  return { data: state.data, error: state.error, loading: state.loading };
}

